/**
 * Phidio signaling protocol — shared between the web client and the signaling server.
 *
 * Transport: a single WebSocket per client. Every frame is JSON-encoded and has a
 * `t` (type) discriminator. Media negotiation follows the "perfect negotiation"
 * pattern: the server assigns exactly one peer in each room `polite: true`.
 */
export const PROTOCOL_VERSION = 3;
/** Maximum number of interest tags a client may submit. */
export const MAX_INTERESTS = 5;
/** Maximum length of a single interest tag. */
export const MAX_INTEREST_LENGTH = 20;
/** Maximum length of a display name. */
export const MAX_NAME_LENGTH = 24;
/** Maximum length of a relayed chat message. */
export const MAX_CHAT_LENGTH = 500;
/** Server -> client heartbeat interval (ms). Clients are dropped after 2 missed beats. */
export const HEARTBEAT_INTERVAL_MS = 20_000;
/** How many spark tokens a client may hold and present on `hello`. */
export const MAX_SPARKS = 12;
/** How long a spark pact stays reconnectable. */
export const SPARK_TTL_MS = 12 * 60 * 60 * 1000;
/** How long a one-sided reconnect intent stays hot before it gives up. */
export const RECONNECT_WINDOW_MS = 45_000;
/** How long a one-sided "stay" invitation stays open. */
export const STAY_WINDOW_MS = 25_000;
/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
export function normalizeInterest(raw) {
    return raw
        .toLowerCase()
        .replace(/^#/, '')
        .replace(/[^a-z0-9+ -]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .slice(0, MAX_INTEREST_LENGTH);
}
export function sanitizeInterests(raw) {
    if (!Array.isArray(raw))
        return [];
    const seen = new Set();
    for (const item of raw) {
        if (typeof item !== 'string')
            continue;
        const tag = normalizeInterest(item);
        if (tag.length > 1)
            seen.add(tag);
        if (seen.size >= MAX_INTERESTS)
            break;
    }
    return [...seen];
}
export function intersect(a, b) {
    const set = new Set(b);
    return a.filter((item) => set.has(item));
}
/** Spark tokens are 32 lowercase hex characters; anything else is dropped. */
export function sanitizeTokens(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        if (typeof item !== 'string')
            continue;
        if (!/^[0-9a-f]{32}$/.test(item))
            continue;
        if (!out.includes(item))
            out.push(item);
        if (out.length >= MAX_SPARKS)
            break;
    }
    return out;
}
//# sourceMappingURL=index.js.map